import React, { useCallback, useEffect, useId, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AutomationPage } from './curation/automation/AutomationPage';
import { useAiSwitch } from './curation/automation/useAiSwitch';
import { TeachTarget } from './curation/automation/WorkflowConfirms';
import { AutomationBanner } from './curation/how/AutomationBanner';
import { HowTab } from './curation/how/HowTab';
import { TeachFocus, TeachTab } from './curation/teach/TeachTab';
import { ToastProvider } from './curation/ToastContext';
import { FinalResponseSupportProvider } from './curation/finalResponseSupport';
import EmailCatalogueList from './email-catalogue/EmailCatalogueList';
import './email-catalogue/emailCatalogue.css';
import './curation/curation.css';

type Tab = 'teach' | 'how' | 'emails';

const TABS: ReadonlyArray<[Tab, string]> = [['teach', 'Teach the AI'], ['how', "How it's doing"]];
const LINK_PARAMS = ['automation', 'workflowId', 'workflowVersion', 'workflowRun'];

const hasAutomationLink = (search: string) => {
  const params = new URLSearchParams(search);
  return LINK_PARAMS.some((name) => params.has(name));
};
const linkedWorkflow = (search: string) => new URLSearchParams(search).get('workflowId');

const CafCorpusManager: React.FC = () => {
  const ids = useId();
  const { isAdmin, hasRole } = useAuth();
  const canCurate = isAdmin || hasRole('corpus-curator');
  // Automation is admin-only (spec Q2); curators see an example's workflow read-only in Teach the AI.
  const canAutomate = isAdmin;
  const [tab, setTab] = useState<Tab>(() => isAdmin && new URLSearchParams(window.location.search).get('section') === 'emails' ? 'emails' : 'teach');
  const tabs: ReadonlyArray<[Tab, string]> = isAdmin ? [...TABS, ['emails', 'Email templates']] : TABS;
  const [automationOpen, setAutomationOpen] = useState(() => canAutomate && hasAutomationLink(window.location.search));
  const [workflowLink, setWorkflowLink] = useState<string | null>(() => (canAutomate ? linkedWorkflow(window.location.search) : null));
  const [queueCount, setQueueCount] = useState<number | null>(null);
  const [focus, setFocus] = useState<TeachFocus | null>(null);
  const aiSwitch = useAiSwitch(canAutomate);
  const pill = aiSwitch.value ? aiSwitch.value.mode : null;

  const clearLink = useCallback(() => {
    if (hasAutomationLink(window.location.search)) window.history.pushState({}, '', window.location.pathname);
    setWorkflowLink(null);
  }, []);
  const chooseTab = useCallback((next: Tab) => {
    clearLink();
    setAutomationOpen(false);
    setTab(next);
  }, [clearLink]);
  const labelSender = useCallback((sender: string) => { setFocus({ sender }); chooseTab('teach'); }, [chooseTab]);
  const reviewSender = useCallback((sender: string) => { setFocus({ sender, preferredCategory: 'approved' }); chooseTab('teach'); }, [chooseTab]);
  const showExample = useCallback(
    (exampleId: string, exampleSender: string) => { setFocus({ exampleId, exampleSender }); chooseTab('teach'); },
    [chooseTab],
  );
  const focusHandled = useCallback(() => setFocus(null), []);
  const openTeach = useCallback((target: TeachTarget | null) => {
    if (target) setFocus({ exampleId: target.entryId, exampleSender: target.sender });
    chooseTab('teach');
  }, [chooseTab]);

  useEffect(() => {
    if (!canAutomate) return undefined;
    const onPop = () => {
      if (hasAutomationLink(window.location.search)) {
        setWorkflowLink(linkedWorkflow(window.location.search));
        setAutomationOpen(true);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [canAutomate]);

  if (!canCurate) {
    return (
      <div className="cur cur-page" role="alert" style={{ padding: 20 }}>
        You don&apos;t have access to AI Curation. Ask an admin if you need it.
      </div>
    );
  }

  const onTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex(([key]) => key === tab);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : null;
    if (next === null) return;
    event.preventDefault();
    chooseTab(tabs[next][0]);
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <ToastProvider>
      {/* Final fix wave C1/C2: one check per page load of whether the backend takes the example final-response setting. */}
      <FinalResponseSupportProvider>
        <div className="cur cur-page">
          <header className="cur-pagehead">
            <h1>AI Curation</h1>
            <p>Similar emails are grouped overnight. Label a group once and it becomes an approved example the AI follows for every email like it.</p>
          </header>
          <div className="cur-tabs">
            <div role="tablist" aria-label="AI Curation sections" className="cur-tablist" onKeyDown={onTabKey}>
              {tabs.map(([key, label]) => {
                const selected = !automationOpen && tab === key;
                return (
                  <button key={key} type="button" role="tab" id={`${ids}-${key}`} aria-controls={`${ids}-panel`}
                    aria-selected={selected} tabIndex={tab === key ? 0 : -1}
                    className={`cur-tab${selected ? ' is-active' : ''}`} onClick={() => chooseTab(key)}>
                    {label}
                    {key === 'teach' && queueCount != null && queueCount > 0 && <span className="cur-tab-count">{queueCount}</span>}
                  </button>
                );
              })}
            </div>
            {canAutomate && (
              <button type="button" className="cur-auto-toggle" aria-pressed={automationOpen} onClick={() => setAutomationOpen(true)}>
                Automation
                {pill && <span className={`cur-pill cur-pill--${pill === 'on' ? 'teal' : pill === 'watching' ? 'blue' : 'grey'}`}>{pill}</span>}
              </button>
            )}
          </div>
          {automationOpen ? (
            <AutomationPage initialWorkflowId={workflowLink} onBack={() => chooseTab('teach')} onOpenTeach={openTeach}
              onSwitchChanged={aiSwitch.reload} />
          ) : (
            <div role="tabpanel" id={`${ids}-panel`} aria-labelledby={`${ids}-${tab}`}>
              {tab === 'emails' ? <div className="email-catalogue"><EmailCatalogueList /></div> : tab === 'teach'
                ? <TeachTab onQueueCount={setQueueCount} focus={focus} onFocusHandled={focusHandled} canAutomate={canAutomate}
                    onOpenWorkflow={(workflowId) => { setWorkflowLink(workflowId); setAutomationOpen(true); }} />
                : (
                  <>
                    <AutomationBanner canOpen={canAutomate} onOpen={() => setAutomationOpen(true)} />
                    <HowTab onLabelSender={labelSender} onReviewSender={reviewSender} onShowExample={showExample} canAutomate={canAutomate} />
                  </>
                )}
            </div>
          )}
        </div>
      </FinalResponseSupportProvider>
    </ToastProvider>
  );
};

export default CafCorpusManager;
